import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Wand2, Loader2, X, Trash2, Trophy, RotateCcw, Upload, Volume2, VolumeX } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { playChime, playCelebration, isMuted as soundIsMuted, setMuted as soundSetMuted } from "@/lib/tokenSounds";

const QUICK_REINFORCERS = [
  "iPad time", "Bubbles", "Trampoline", "Sticker", "M&Ms",
  "High five", "Puzzle", "Slime", "Play-Doh", "Toy car",
];

/**
 * TokenBoardControl — a floating, always-accessible reward panel usable from
 * INSIDE Match / Sort runners. Lets the BCBA:
 *   • Tap "+1" to award a token without needing a correct answer
 *   • Reset the board mid-session
 *   • Change the reinforcer + regenerate themed tokens without exiting
 *
 * All state is lifted into the parent — TokenBoardControl just fires setters.
 */
export default function TokenBoardControl({
  tokens, reinforcerImage, reinforcerLabel, setTokens, setReinforcerLabel, setReinforcerImage,
  tokensEarned, setTokensEarned, onCelebrate,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [muted, setMutedState] = useState(() => soundIsMuted());
  const [tokenCount, setTokenCount] = useState(tokens?.length || 5);
  const [draftLabel, setDraftLabel] = useState(reinforcerLabel || "");
  const [draftImage, setDraftImage] = useState(reinforcerImage || "");
  const fileRef = useRef(null);

  const hasBoard = tokens && tokens.length > 0;
  const complete = hasBoard && tokensEarned >= tokens.length;

  const toggleMute = () => {
    const next = !muted;
    soundSetMuted(next);
    setMutedState(next);
    if (!next) playChime(); // brief confirmation cue when un-muting
    toast(next ? "Sound off" : "Sound on", { duration: 900 });
  };

  const awardToken = () => {
    if (!hasBoard) {
      toast.info("Set a reinforcer first — tap Reward.");
      setOpen(true);
      return;
    }
    if (complete) return;
    const next = tokensEarned + 1;
    setTokensEarned(next);
    if (next >= tokens.length) { onCelebrate?.(); }
    else { playChime(); }
  };

  const resetBoard = () => {
    setTokensEarned(0);
    toast.success("Board reset");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image.");
    if (f.size > 3_000_000) return toast.error("Image too large — 3 MB max.");
    const reader = new FileReader();
    reader.onload = () => setDraftImage(reader.result);
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    const label = draftLabel.trim();
    if (!label) return toast.error("Name the reinforcer first.");
    setBusy(true);
    try {
      const r = await http.post("/generate-tokens", { reinforcer_label: label, count: tokenCount });
      setTokens(r.data.tokens || []);
      setReinforcerLabel(label);
      setReinforcerImage(draftImage || "");
      setTokensEarned(0);
      toast.success(`${tokenCount} themed tokens ready`);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not generate tokens");
    } finally { setBusy(false); }
  };

  const clearBoard = () => {
    setTokens([]);
    setReinforcerLabel("");
    setReinforcerImage("");
    setTokensEarned(0);
    setDraftLabel("");
    setDraftImage("");
    if (fileRef.current) fileRef.current.value = "";
    toast.success("Reinforcer cleared");
    setOpen(false);
  };

  return (
    <>
      {/* Floating action cluster — sticks bottom-right of every runner screen */}
      <div className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 flex items-end gap-2" data-testid="token-board-control">
        {hasBoard && (
          <button
            onClick={awardToken}
            disabled={complete}
            className={`h-14 pl-4 pr-5 rounded-full shadow-xl flex items-center gap-2 font-bold text-white transition-transform active:scale-95 ${
              complete ? "bg-emerald-500" : "bg-amber-500 hover:bg-amber-600"
            }`}
            data-testid="token-award-button"
            aria-label="Award a token"
          >
            <Plus className="w-5 h-5" />
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-widest opacity-90">Award</div>
              <div className="text-sm">{tokensEarned}/{tokens.length}</div>
            </div>
          </button>
        )}
        <button
          onClick={() => setOpen(true)}
          className="h-14 w-14 rounded-full bg-slate-900 text-white shadow-xl flex items-center justify-center hover:bg-slate-800"
          data-testid="token-board-open"
          aria-label="Open reward settings"
        >
          <Trophy className="w-5 h-5" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
            onClick={() => !busy && setOpen(false)}
            data-testid="token-board-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-600" />
                  <div className="font-display font-extrabold text-lg">Token board</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={toggleMute}
                    className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                      muted ? "bg-slate-100 text-slate-500 hover:text-slate-700" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    }`}
                    data-testid="token-sound-toggle"
                    aria-label={muted ? "Unmute sound" : "Mute sound"}
                    title={muted ? "Sound off — tap to enable" : "Sound on — tap to mute"}
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 h-8 w-8 flex items-center justify-center" data-testid="token-board-close" aria-label="Close">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {hasBoard && (
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Current reward</div>
                        <div className="font-bold text-slate-900">{reinforcerLabel}</div>
                      </div>
                      {reinforcerImage && (
                        <img src={reinforcerImage} alt={reinforcerLabel} className="w-14 h-14 rounded-xl object-cover ring-2 ring-emerald-300" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-amber-200">
                        <div
                          className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all"
                          style={{ width: `${(tokensEarned / tokens.length) * 100}%` }}
                          data-testid="token-board-progress-fill"
                        />
                      </div>
                      <div className="text-xs font-bold text-slate-700 tabular-nums">{tokensEarned} / {tokens.length}</div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button onClick={awardToken} disabled={complete} className="rounded-full flex-1" data-testid="token-board-award-inline">
                        <Plus className="w-4 h-4 mr-1.5" /> {complete ? "Complete!" : "Award token"}
                      </Button>
                      <Button onClick={resetBoard} variant="outline" size="icon" className="rounded-full" data-testid="token-board-reset" aria-label="Reset board">
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button onClick={clearBoard} variant="outline" size="icon" className="rounded-full text-rose-600" data-testid="token-board-clear" aria-label="Clear reinforcer">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">
                    {hasBoard ? "Change reinforcer" : "Set a reinforcer"}
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {QUICK_REINFORCERS.map((r) => (
                      <button
                        key={r}
                        onClick={() => setDraftLabel(r)}
                        className={`px-2.5 h-7 rounded-full text-xs font-semibold border transition-colors ${
                          draftLabel === r
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                        data-testid={`quick-reinforcer-${r.toLowerCase().replace(/[^a-z]/g,'-')}`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>

                  <Input
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    placeholder="Reinforcer name — e.g. iPad time"
                    maxLength={80}
                    className="rounded-xl"
                    data-testid="token-modal-label-input"
                  />

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 cursor-pointer hover:text-slate-900">
                      <Upload className="w-3.5 h-3.5" />
                      {draftImage ? "Change photo" : "Upload photo"}
                      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="token-modal-file-input" />
                    </label>
                    {draftImage && (
                      <>
                        <img src={draftImage} alt="preview" className="w-10 h-10 rounded-lg object-cover ring-1 ring-slate-200" />
                        <button onClick={() => setDraftImage("")} className="text-xs text-rose-600 hover:underline">Remove</button>
                      </>
                    )}
                    <div className="ml-auto flex items-center gap-1.5">
                      <span className="text-[11px] text-slate-500">Tokens</span>
                      <Select value={String(tokenCount)} onValueChange={(v) => setTokenCount(parseInt(v, 10))}>
                        <SelectTrigger className="w-16 h-8 rounded-xl text-xs" data-testid="token-modal-count-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[3, 4, 5, 6, 7, 8, 10].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={generate}
                    disabled={busy || !draftLabel.trim()}
                    className="rounded-full w-full mt-3"
                    data-testid="token-modal-generate-button"
                  >
                    {busy
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating themed tokens…</>
                      : <><Wand2 className="w-4 h-4 mr-1.5" /> {hasBoard ? "Regenerate & apply" : "Generate & start board"}</>
                    }
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
